import htmx from 'htmx.org';
import { getBoolean, isEmpty, htmlEncode } from '../utilities';

export function bindToggles(content?: Element | Document | null | undefined) {
    // look for [data-toggle].
    //  if data-toggle == open, the toggle only opens the target
    //  if data-toggle == dismiss, the toggle ony dismisses the target
    // [data-target] sets the id of the element to toggle. use "," as a separator if you need to toggle more than one element.
    // [data-expanded] (set to true or false) gives current shown/hidden status of the element
    // [data-reload-dismiss] (set to true or false) tells the browser to reload when the element is dismissed.
    // [data-toggle-group] (set all items in a group to the same value) all items in the group will be dismissed except for the element being toggled.
    // [data-scrolltop] (set to true, false, or omit) if true, puts the toggled element at the top of the page.
    // [data-event] (set to js event or omit) defaults to click.
    // [data-blur-toggle] if present, closes the toggled element when a click is registered outside of the element
    // [data-toggle-class] (defaults to 'hidden') sets the class being toggled on the target element.
    // [data-toggle-icon] (defaults to none) The id of the element holding the open and dismiss icons.
    // [data-open-icon] The icon to display on the toggle element when expanded. Specify the icon name from sprite.svg. (defaults to no icon)
    // [data-dismiss-icon] The icon to display on the toggle element when not expanded. Specify the icon name from sprite.svg. (defaults to no icon)
    // [data-open-class] (defaults to the value of the class attribute) The class to apply to the toggle element when expanded.
    // [data-dismiss-class] (defaults to none) The class to apply to the toggle element when dismissed.
    if (!content) {
        content = document;
    }
    content.querySelectorAll<HTMLElement>("[data-toggle]").forEach(toggle => {
        const tEvent = toggle.getAttribute('toggle-event');

        if (tEvent === null || tEvent !== 'true') {
            toggle.setAttribute('toggle-event', 'true');
            if (toggle.getAttribute('aria-controls') === null) {
                toggle.setAttribute('aria-controls', toggle.getAttribute('data-target') ?? "");
            }

            toggle.addEventListener(toggle.getAttribute('data-event') ?? "click", evt => {
                evt.preventDefault();
                toggleEvent(toggle);
            });

            if (toggle.hasAttribute("data-blur-toggle")) {
                document.addEventListener("click", e => checkForBlurToggle(toggle, e));
            }

        }
    });
    content.querySelectorAll<HTMLElement>('[data-expanded="true"]').forEach(elem => {
        const toggle = document.querySelector<HTMLElement>(`[data-target=${elem.id}]`);
        if (!toggle) {
            if (elem instanceof HTMLDialogElement) {
                showElement(elem, "hidden");
            }
            return;
        }
        toggleElement(toggle, true);
    });
}

function checkForBlurToggle(toggle: HTMLElement, event: Event) {
    const toggleTarget = document.getElementById(toggle.getAttribute("data-target") ?? "");
    if (toggle.getAttribute("aria-expanded") === "true" && event.target !== toggle && event.target as HTMLElement !== toggleTarget) {
        toggleEvent(toggle);
    }
}

function toggleEvent(elem: HTMLElement) {
    toggleElement(elem);
    toggleGroup(elem, elem.getAttribute("data-toggle-group"));
    const scrollTop = elem.getAttribute('data-scrolltop');
    if (scrollTop !== null && scrollTop === 'true') {
        elem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
}

function toggleElement(elem: HTMLElement, open: boolean | null = null) {
    let targetIds = elem.getAttribute("data-target")?.split(",");
    if (targetIds === undefined) { return };
    open = (open === null) ? getToggleOption(elem.getAttribute('data-toggle')) : open;
    let toggleClass = "hidden"
    if (elem.hasAttribute("data-toggle-class")) {
        let toggleClassAttribute = elem.getAttribute("data-toggle-class");
        toggleClass = toggleClassAttribute ?? "";
    }
    let toggleIcon: HTMLElement | null = null;
    let openIcon = elem.getAttribute('data-open-icon') ?? '';
    let dismissIcon = elem.getAttribute('data-dismiss-icon') ?? '';
    let iconClass = elem.getAttribute('data-icon-class') ?? '';
    if (elem.hasAttribute('data-toggle-icon')) {
        toggleIcon = document.querySelector<HTMLElement>(`#${elem.getAttribute('data-toggle-icon')}`);
    }
    targetIds.forEach(id => {
        let target = document.querySelector<HTMLElement>(`#${id.trim()}`);
        if (target === null) { return };
        let expanded = (open !== null) ? open : !getBoolean(target.getAttribute('data-expanded'));

        if (!elem.hasAttribute('data-dismiss-class') && elem.hasAttribute('data-open-class')) {
            elem.setAttribute('data-dismiss-class', elem.className);
        }
        elem.setAttribute('aria-expanded', `${expanded}`);

        if (expanded) {
            showElement(target, toggleClass, toggleIcon, openIcon, iconClass);
            if (elem.hasAttribute('data-open-class')) {
                elem.setAttribute('class', elem.getAttribute('data-open-class') ?? '');
            }
        }
        else {
            dismissElement(target, toggleClass, getBoolean(elem.getAttribute('data-reload-dismiss')), toggleIcon, dismissIcon, iconClass);
            if (elem.hasAttribute('data-dismiss-class')) {
                elem.setAttribute('class', elem.getAttribute('data-dismiss-class') ?? '');
            }
        }
    });
}

function toggleGroup(elem: HTMLElement, group: string | null) {
    if (group === null) {
        return;
    }
    document.querySelectorAll<HTMLElement>(`[data-toggle-group="${group}"]`).forEach(grpElem => {
        if (grpElem === elem) {
            return;
        }
        toggleElement(grpElem, false);
    });
}

export function showElement(target: HTMLElement, toggleClass: string = "hidden", iconElem: HTMLElement | null = null, openIcon: string = "", iconClass: string = "") {
    if (target == null) {
        console.debug("No element found to open");
        return;
    }
    if (target.tagName.toLowerCase() === 'dialog') {
        console.debug('showing dialog', target);
        (<HTMLDialogElement>target).showModal();
        return;
    }
    target.setAttribute("data-expanded", "true");
    if (toggleClass && target.classList.contains(toggleClass)) {
        target.classList.remove(toggleClass);
    }
    if (iconElem && !isEmpty(openIcon)) {
        iconElem.innerHTML = `<svg class="${htmlEncode(iconClass)}" role="presentation"><use xlink:href="/img/icons/sprite.svg#${encodeURIComponent(openIcon)}"></use></svg>`;
    }
}

export function dismissElement(target: HTMLElement, toggleClass: string = "hidden", reload: boolean = false, iconElem: HTMLElement | null = null, dismissIcon: string = "", iconClass: string = "") {
    if (reload) { location.reload(); }
    if (target == null) {
        console.debug("No element found to dismiss");
        return;
    }
    if (target.tagName.toLowerCase() === 'dialog') {
        (<HTMLDialogElement>target).close();
        return;
    }
    target.setAttribute("data-expanded", "false");
    if (toggleClass) {
        target.classList.add(toggleClass);
    }
    if (iconElem && !isEmpty(dismissIcon)) {
        iconElem.innerHTML = `<svg class="${htmlEncode(iconClass)}" role="presentation"><use xlink:href="/img/icons/sprite.svg#${encodeURIComponent(dismissIcon)}"></use></svg>`;
    }
}

function getToggleOption(value: string | null) {
    if (value === null) {
        return null;
    }

    if (value.toLowerCase() === 'open') {
        return true;
    }
    else if (value.toLowerCase() === 'dismiss') {
        return false;
    }
    else {
        return null;
    }
}

document.addEventListener('DOMContentLoaded', (event) => {
    bindToggles(document);
});

document.addEventListener("htmx:afterSettle", (event) => {
    bindToggles((event as CustomEvent).detail.elt);
});
